// ─────────────────────────────────────────────────────────────────────────────
//  pushNotifications.js  —  Torch App · iOS Native Push Notification Manager
//
//  Companion to notifications.js (which handles web push via FCM).
//  This file handles native iOS push via Capacitor + APNs:
//
//  1. Permission request (only on native iOS — no-op on web)
//  2. APNs token registration
//  3. Token storage in Firestore (same collection as web tokens, tagged)
//  4. Foreground notification display
//  5. Tap handling (action performed)
//
//  Tokens are stored at users/{uid}/fcmTokens/{token} with platform field,
//  same path as web tokens, so one Cloud Function can target all devices.
// ─────────────────────────────────────────────────────────────────────────────

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { getFirestore, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import app from './firebase';

const db = getFirestore(app);

let isRegistering = false;
let listenersAttached = false;

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Register the current device for iOS push notifications and save the APNs
 * token to Firestore so Cloud Functions can target this user.
 *
 * No-op on web (web push is handled separately by notifications.js).
 * Safe to call multiple times — early-returns on duplicate calls.
 */
export async function registerIosPushNotifications(userId) {
  if (!Capacitor.isNativePlatform()) {
    return; // web build — handled by notifications.js
  }
  if (!userId) {
    console.warn('[ios-push] called without userId — skipping');
    return;
  }
  if (isRegistering) return;
  isRegistering = true;

  try {
    if (!listenersAttached) {
      attachListeners(userId);
      listenersAttached = true;
    }

    const permStatus = await PushNotifications.checkPermissions();
    let perm = permStatus.receive;

    if (perm === 'prompt' || perm === 'prompt-with-rationale') {
      const requested = await PushNotifications.requestPermissions();
      perm = requested.receive;
    }

    if (perm !== 'granted') {
      console.log('[ios-push] permission not granted:', perm);
      return;
    }

    // Triggers the 'registration' event listener once APNs returns a token
    await PushNotifications.register();
  } catch (err) {
    console.error('[ios-push] registration error:', err);
  } finally {
    isRegistering = false;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// LISTENERS
// ─────────────────────────────────────────────────────────────────────────────

function attachListeners(userId) {
  // APNs handed us back a device token — store it in Firestore
  PushNotifications.addListener('registration', async (token) => {
    try {
      const tokenValue = token.value;
      // Use the token itself as the doc ID (same convention as web tokens)
      await setDoc(
        doc(db, 'users', userId, 'fcmTokens', tokenValue),
        {
          token: tokenValue,
          platform: 'ios-native',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      console.log('[ios-push] device token saved');
    } catch (err) {
      console.error('[ios-push] failed to save token:', err);
    }
  });

  // APNs registration failed entirely
  PushNotifications.addListener('registrationError', (err) => {
    console.error('[ios-push] APNs registration error:', err);
  });

  // Push arrived while app is in the foreground (iOS suppresses banner by default)
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('[ios-push] foreground notification:', notification);
    // (optional) show in-app toast or update a badge here
  });

  // User tapped a notification (from background or terminated state)
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    console.log('[ios-push] notification tapped:', action);
    // (optional) navigate to the right screen based on action.notification.data
  });
}
